<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div class="m-5 pb-5">
    <ButtonPanel
      :buttonIcon="'refresh'"
      :click="refresh"
      icon="group"
      :title="`${$t(`message.modelAnswer`)} (${programLanguage})`"
    >
      <div v-if="sortedModelAnswerList.length > 0">
        <ModelAnswerListHeader />
        <div v-for="modelAnswer in sortedModelAnswerList" :key="modelAnswer.judgeId">
          <div
            v-if="hasDataInExpandedList(modelAnswer.judgeId).value && hasDataInLoadingList(modelAnswer.judgeId).value"
          >
            <NestedElement
              :isSummaryExpanded="modelAnswerExpandedList[modelAnswer.judgeId]"
              :loading="modelAnswerLoadingList[modelAnswer.judgeId]"
              @clickSummary="clickSummary(modelAnswer.judgeId)"
            >
              <template #labels>
                <div :class="`w-full grid grid-cols-11`">
                  <div class="px-2 py-2 my-auto flex items-center justify-center">
                    <span v-if="modelAnswer.policy == 'public'" class="material-icons text-accept"> visibility </span>
                    <span v-else class="material-icons text-red-600"> visibility_off </span>
                  </div>
                  <div class="px-2 py-3 my-auto text-center col-span-2">
                    {{ modelAnswer.judgeId }}
                  </div>
                  <div class="px-2 py-3 my-auto text-center col-span-2">{{ modelAnswer.userId }}</div>
                  <div class="px-2 py-3 my-auto text-center col-span-2">{{ modelAnswer.rating.toFixed(2) }}</div>
                  <div class="px-2 py-3 my-auto text-center col-span-2">
                    {{ (modelAnswer.cpuTime / 100).toFixed(2) }} sec
                  </div>
                  <div class="px-2 py-3 my-auto text-center col-span-2">{{ modelAnswer.codeSize }} B</div>
                </div>
              </template>
              <template v-slot:body>
                <div v-if="modelAnswerSourceCodeList[modelAnswer.judgeId]">
                  <div class="px-3 py-3" style="height: 400px">
                    <MonacoEditorDiff
                      v-if="modelAnswerIsDiffList[modelAnswer.judgeId]"
                      class="h-full border dark:border-dark-400"
                      :modelSourceCode="modelAnswerSourceCodeList[modelAnswer.judgeId]"
                    />
                    <MonacoEditorRead
                      v-else
                      class="h-full border dark:border-dark-400"
                      :value="modelAnswerSourceCodeList[modelAnswer.judgeId]"
                    />
                  </div>
                  <div class="w-full flex items-center justify-center pb-3">
                    <IconTextButton
                      :border_radius="2"
                      class="mx-1"
                      :font_weight="'700'"
                      :icon="'difference'"
                      :iconSize="'text-xl'"
                      :padding_bottom="4"
                      :padding_left="12"
                      :padding_right="12"
                      :padding_top="4"
                      :text="'Diff'"
                      :textSize="'text-base'"
                      @click="reverseModelAnswerIsDiffFlag(modelAnswer.judgeId)"
                    />
                    <IconTextButton
                      :border_radius="2"
                      class="mx-1"
                      :font_weight="'700'"
                      :icon="'content_copy'"
                      :iconSize="'text-xl'"
                      :padding_bottom="4"
                      :padding_left="12"
                      :padding_right="12"
                      :padding_top="4"
                      :text="$t('message.copy')"
                      :textSize="'text-base'"
                      @click="copyClipboard(modelAnswerSourceCodeList[modelAnswer.judgeId])"
                    />
                  </div>
                </div>
              </template>
            </NestedElement>
          </div>
        </div>
      </div>
      <div v-else-if="sortedModelAnswerList.length <= 0 && !modelAnswerListLoading" class="p-3">
        {{ $t('message.noData') }}
      </div>
      <div v-if="modelAnswerListLoading" class="flex justify-center items-center" style="height: 120px">
        <LoadingPanel />
      </div>
      <div v-else-if="modelAnswerListError" class="p-3">{{ $t('message.error') }}</div>
    </ButtonPanel>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted } from 'vue'

import IconTextButton from '@/presentation/components/Button/IconTextButton.vue'
import NestedElement from '@/presentation/components/Element/NestedElement.vue'
import LoadingPanel from '@/presentation/components/Loading/LoadingPanel.vue'
import ModelAnswerListHeader from '@/presentation/components/ModelAnswer/children/ModelAnswerListHeader.vue'
import MonacoEditorDiff from '@/presentation/components/MonacoEditor/MonacoEditorDiff.vue'
import MonacoEditorRead from '@/presentation/components/MonacoEditor/MonacoEditorRead.vue'
import ButtonPanel from '@/presentation/components/Panel/ButtonPanel.vue'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useModelAnswerListPresenter } from '@/presentation/composables/modelAnswer/useModelAnswerListPresenter'
import { useModelAnswerPageStatePresenter } from '@/presentation/composables/modelAnswer/useModelAnswerPageStatePresenter'
import { useModelAnswerPresenter } from '@/presentation/composables/modelAnswer/useModelAnswerPresenter'
import { useModelAnswerSourceCodePresenter } from '@/presentation/composables/modelAnswer/useModelAnswerSourceCodePresenter'
import { useProblemStatePresenter } from '@/presentation/composables/problem/useProblemStatePresenter'

export default defineComponent({
  components: {
    MonacoEditorRead,
    MonacoEditorDiff,
    ModelAnswerListHeader,
    ButtonPanel,
    LoadingPanel,
    NestedElement,
    IconTextButton
  },
  setup(props, ctx) {
    const { problemId } = useProblemStatePresenter()

    const { copyClipboard, programLanguage } = useConfigStatePresenter()
    const { initState, clickSummary } = useModelAnswerPresenter()
    const {
      modelAnswerExpandedList,
      modelAnswerLoadingList,
      modelAnswerIsDiffList,
      isFirstOpendPage,
      hasDataInExpandedList,
      hasDataInLoadingList,
      setIsFirstOpendPage,
      reverseModelAnswerIsDiffFlag
    } = useModelAnswerPageStatePresenter()
    const {
      modelAnswerList,
      hasModelAnswerInList,
      loading: modelAnswerListLoading,
      error: modelAnswerListError
    } = useModelAnswerListPresenter()
    const {
      modelAnswerSourceCodeList,
      loading: modelAnswerSourceCodeLoading,
      error: modelAnswerSourceCodeError
    } = useModelAnswerSourceCodePresenter()

    const sortedModelAnswerList = computed(() => {
      const answerArray = Object.values(modelAnswerList.value)
      return answerArray.sort((a, b) => b.rating - a.rating)
    })

    const refresh = () => {
      initState(problemId.value, programLanguage.value)
    }

    if (isFirstOpendPage.value) refresh()

    onMounted(() => {
      setIsFirstOpendPage(false)
    })

    return {
      sortedModelAnswerList,
      modelAnswerList,
      modelAnswerListLoading,
      modelAnswerListError,
      hasModelAnswerInList,
      modelAnswerSourceCodeList,
      modelAnswerSourceCodeLoading,
      modelAnswerSourceCodeError,
      modelAnswerLoadingList,
      modelAnswerExpandedList,
      modelAnswerIsDiffList,
      hasDataInExpandedList,
      hasDataInLoadingList,
      refresh,
      clickSummary,
      reverseModelAnswerIsDiffFlag,
      programLanguage,
      copyClipboard
    }
  }
})
</script>

<style lang="postcss" scoped>
.note-box {
  //padding: 0.5em 1em 0.7em 1em;
  margin: 0em 0 1em 0;
}

.top {
  user-select: none;
}
.top-dark {
  user-select: none;
}
.top_in {
  padding: 0.7em 1em 0.7em 1em;
  //color: #5d627b;
}
.click-effect:active {
  @apply bg-gray-600;
}

.loading::after {
  content: '';
  position: absolute;

  width: 5em;
  height: 5em;
  top: 40vh;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border: 5px white solid;
  border-top: 5px lightgray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}
.loading-dark::after {
  content: '';
  position: absolute;

  width: 5em;
  height: 5em;
  top: 40vh;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border: 5px #1e1e1e solid;
  border-top: 5px lightgray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

.loadingCode::after {
  content: '';
  position: absolute;

  width: 5em;
  height: 5em;
  top: 0px;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border: 5px white solid;
  border-top: 5px lightgray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes circ {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
.loadingCode-dark::after {
  content: '';
  position: absolute;

  width: 5em;
  height: 5em;
  top: 0px;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  border: 5px #3e3e3e solid;
  border-top: 5px lightgray solid;
  border-radius: 50%;
  animation-name: circ;
  animation-delay: 0ms;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes circ {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

.hover-copy {
  text-decoration: none;

  @apply relative inline-block;

  &::before {
    content: '';
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #232a3b;
    @apply block absolute fadein;
  }

  &::after {
    font-family: sans-serif;
    content: attr(data-tooltip);
    bottom: 165%;
    left: 50%;
    transform: translateX(-50%);
    @apply bg-aoj text-white rounded px-2 py-1 block absolute font-bold text-sm pointer-events-none z-10 fadein;
  }
}

.hover-copy-dark {
  text-decoration: none;

  @apply relative inline-block;

  &::before {
    content: '';
    top: -15px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid white;
    @apply block absolute fadein;
  }

  &::after {
    font-family: sans-serif;
    content: attr(data-tooltip);
    bottom: 165%;
    left: 50%;
    transform: translateX(-50%);
    @apply bg-black border text-dark-text rounded px-2 py-1 block absolute font-bold text-sm pointer-events-none z-10 fadein;
  }
}

.fadein {
  opacity: 1;
  animation-name: fadeout;
  animation-duration: 1s;
  animation-fill-mode: forwards;
  animation-delay: 1s;
}

@keyframes fadeout {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.select {
  user-select: none;
}
.click-effect:active {
  @apply border-gray-900 bg-gray-200;
}

.click-effect-dark:active {
  @apply bg-dark-10;
}
</style>
